import * as THREE from "three";
import type { MapDef } from "@rietz/shared";
import { makeLabel, type Label } from "../debug/labels.js";
import { cssHex, poiColor } from "../render/poiPalette.js";

/**
 * Floating names over the village's points of interest — the signposts that
 * make identical alpine houses navigable: one sprite per POI, clear of the
 * rooftop, coloured by kind (`render/poiPalette.ts`). Shown within
 * `VISIBLE_RANGE_M` of the camera (F5 flips `setShowAll`), drawn with the depth
 * buffer so nothing hovers through a mountain. `makeLabel` returns `null`
 * without a DOM, so a headless build has a group with no children.
 */

export interface PoiLabels {
  group: THREE.Group;
  /** Sprites actually built. 0 without a DOM, and 0 for a map with no POIs. */
  labelCount: number;
  /** Culls far names and keeps the rest legible; once per rendered frame. */
  update(cameraPos: THREE.Vector3): void;
  /**
   * Debug (F5): ignore the range cull and show every name. Takes effect on the
   * next `update`.
   */
  setShowAll(showAll: boolean): void;
  dispose(): void;
}

/**
 * How close the camera must be for a name to show, metres — the street you are
 * on and the next one over.
 */
const VISIBLE_RANGE_M = 80;

/** Minimum height above the POI's ground position, metres. */
const ANCHOR_HEIGHT_M = 5;

/**
 * A POI marker stands at the door or centroid, so a fixed anchor would put the
 * sprite inside the facade. The scan lifts the anchor above the tallest solid
 * near the marker: how far sideways counts as this building, how much air above
 * the roof, and a ceiling so a silo or church tower does not put the label in
 * the sky.
 */
const ROOF_SCAN_RADIUS_M = 5;
const ROOF_CLEARANCE_M = 1.5;
const ROOF_SCAN_MAX_M = 40;

/** Anchor height at (poi): above every nearby roof, and never below 5 m. */
function anchorY(map: MapDef, pos: { x: number; y: number; z: number }): number {
  let top = -Infinity;
  for (const s of map.solids) {
    const b = s.box;
    if (b.min.x > pos.x + ROOF_SCAN_RADIUS_M || b.max.x < pos.x - ROOF_SCAN_RADIUS_M) continue;
    if (b.min.z > pos.z + ROOF_SCAN_RADIUS_M || b.max.z < pos.z - ROOF_SCAN_RADIUS_M) continue;
    // Only solids overhead matter: ignore the ground underfoot (and terraces
    // below), and anything implausibly tall for "the building this names".
    if (b.max.y <= pos.y + 2.2 || b.max.y > pos.y + ROOF_SCAN_MAX_M) continue;
    if (b.max.y > top) top = b.max.y;
  }
  const base = pos.y + ANCHOR_HEIGHT_M;
  return top === -Infinity ? base : Math.max(base, top + ROOF_CLEARANCE_M);
}

/** World height of the sprite at close range, metres. */
const BASE_HEIGHT_M = 2.2;

/**
 * Floor on the label's ANGULAR size: beyond `BASE_HEIGHT_M / ANGULAR_FLOOR`
 * (~79 m) the sprite grows with distance. Barely fires in normal play; earns
 * its keep in show-all mode.
 */
const ANGULAR_FLOOR = 0.028;

/** …but never so large it becomes a billboard over the whole valley. */
const MAX_HEIGHT_M = 26;

const FONT_PX = 44;

interface Entry {
  sprite: THREE.Sprite;
  /** Canvas aspect, captured at build time so rescaling never distorts. */
  aspect: number;
  dispose(): void;
}

export function buildPoiLabels(map: MapDef): PoiLabels {
  const group = new THREE.Group();
  group.name = `pois:${map.id}`;

  const entries: Entry[] = [];
  let showAll = false;
  for (const poi of map.pois ?? []) {
    if (poi.name.length === 0) continue;
    const label: Label | null = makeLabel(poi.name, {
      color: cssHex(poiColor(poi.kind)),
      heightM: BASE_HEIGHT_M,
      fontPx: FONT_PX,
    });
    if (label === null) continue;
    label.sprite.position.set(poi.pos.x, anchorY(map, poi.pos), poi.pos.z);
    group.add(label.sprite);
    entries.push({
      sprite: label.sprite,
      aspect: label.sprite.scale.y === 0 ? 1 : label.sprite.scale.x / label.sprite.scale.y,
      dispose: label.dispose,
    });
  }

  return {
    group,
    labelCount: entries.length,

    update(cameraPos: THREE.Vector3): void {
      if (entries.length === 0) return;
      for (const e of entries) {
        const p = e.sprite.position;
        const dx = p.x - cameraPos.x;
        const dy = p.y - cameraPos.y;
        const dz = p.z - cameraPos.z;
        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
        const visible = showAll || dist <= VISIBLE_RANGE_M;
        e.sprite.visible = visible;
        if (!visible) continue;
        let h = dist * ANGULAR_FLOOR;
        if (!(h > BASE_HEIGHT_M)) h = BASE_HEIGHT_M;
        else if (h > MAX_HEIGHT_M) h = MAX_HEIGHT_M;
        e.sprite.scale.set(h * e.aspect, h, 1);
      }
    },

    setShowAll(on: boolean): void {
      showAll = on;
    },

    dispose(): void {
      for (const e of entries) e.dispose();
      entries.length = 0;
      group.clear();
    },
  };
}
